// The old site, read live over the bridge (API: GET /legacy/old-site, POST /legacy/old-account).
// Nothing is fetched until staff ask: the search on Old accounts, or "Load from the old site" on a
// claim. Every lookup is in the audit log. The old site never sends payout destinations, gift codes,
// IP addresses, devices, phone numbers or passwords.
import { useState, type FormEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CloudDownload, Search } from 'lucide-react';
import { api } from '../lib/api';
import {
  ErrorNote,
  fmtNum,
  fmtUsd,
  KV,
  Mono,
  Pill,
  ScrollX,
  SkeletonRows,
  StatusPill,
  TimeAgo,
  UserChip,
} from './ui';

type Scalar = string | number | boolean | null | undefined;
type Row = Record<string, Scalar>;

interface OldSite {
  configured: boolean;
  ok: boolean;
  ms?: number;
  clockSkewSeconds?: number;
  missingIndexes?: string[];
  error?: string;
}

interface Numbers {
  balanceCoins: number;
  lifetimeEarnedCoins: number;
  lifetimeWithdrawnCoins: number;
  pendingCoins: number;
  earningsCount: number;
  withdrawalsCount: number;
  reversalsCount: number;
  lastEarningAt: number | null;
  lastWithdrawalAt: number | null;
  ruleLastEarningAt: number | null;
  ruleLastWithdrawalAt: number | null;
  anomalies: { unreadableAmounts: number; negativeWithdrawals: number; unmatchedReversals: number };
}

type Lookup =
  | { found: false; ambiguous: boolean }
  | {
      found: true;
      ambiguous: boolean;
      old: {
        legacyUserId: string;
        email: string;
        username: string | null;
        createdAt: number | null;
        state: 'active' | 'deleted' | 'banned' | 'blocked';
        profile: Row;
        recentEarnings: Row[];
        recentWithdrawals: Row[];
        rowsRead: { earnings: number; withdrawals: number };
        truncated: boolean;
        linkedTo: { newUserId: string; state: string | null; coins: number | null } | null;
      };
      numbers: Numbers;
      ifLinkedNow:
        { refused: string } | { status: 'no_coins' | 'credited' | 'pending_review'; coins: number };
      here: {
        claimStatus: string | null;
        claimCoins: number | null;
        claimedAt: number | null;
        balanceCoins: number;
        userId: string;
        username: string;
      } | null;
      pendingReview: PendingReview | null;
    };

interface PendingSignal {
  tone: 'warn' | 'info';
  text: string;
}

interface PendingReview {
  items: {
    id: string;
    oldEarningId: string;
    coins: number;
    status: string;
    what: string | null;
    network: string | null;
    earnedAt: number | null;
    note: string | null;
    signals: PendingSignal[];
  }[];
  account: PendingSignal[];
  advice: { clear: boolean; text: string } | null;
}

type Query = { email: string } | { legacyUserId: string } | { userId: string };

function useOldAccount(q: Query | null) {
  return useQuery({
    queryKey: ['old-account', q],
    queryFn: () => api<Lookup>('/legacy/old-account', { body: q }),
    enabled: !!q,
    // Each lookup reads the old database and is audited: no background refetching.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** One line: is the old site reachable with our secret? */
export function OldSiteStatus() {
  const { data, isLoading } = useQuery({
    queryKey: ['old-site'],
    queryFn: () => api<OldSite>('/legacy/old-site'),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  if (isLoading || !data)
    return <span className="text-[13px] text-stone">Checking the old site…</span>;
  if (!data.configured)
    return (
      <span className="text-[13px] text-stone">
        <Pill tone="sun">Not set up</Pill> LEGACY_BRIDGE_URL is empty in apps/api/wrangler.jsonc.
      </span>
    );
  if (!data.ok)
    return (
      <span className="text-[13px] text-berry-deep">
        <Pill tone="berry">Unreachable</Pill> {data.error}
      </span>
    );
  return (
    <span className="text-[13px] text-stone">
      <Pill tone="mint">Connected</Pill> {fmtNum(data.ms ?? 0)} ms
      {Math.abs(data.clockSkewSeconds ?? 0) > 60 && (
        <b className="text-berry-deep"> · clocks {data.clockSkewSeconds} s apart</b>
      )}
      {!!data.missingIndexes?.length && (
        <b className="text-berry-deep">
          {' '}
          · no userid index on {data.missingIndexes.join(' and ')}: lookups will be slow
        </b>
      )}
    </span>
  );
}

/** Search box for the Old accounts page: an old email or old user id. */
export function OldAccountSearch() {
  const [text, setText] = useState('');
  const [q, setQ] = useState<Query | null>(null);
  const result = useOldAccount(q);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v = text.trim();
    if (/^[0-9a-f]{24}$/i.test(v)) setQ({ legacyUserId: v.toLowerCase() });
    else if (v.includes('@')) setQ({ email: v });
  };
  const valid = /^[0-9a-f]{24}$/i.test(text.trim()) || /^\S+@\S+\.\S+$/.test(text.trim());
  return (
    <section className="card" aria-label="Look up an old account">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-hairline px-4 py-3 sm:px-5">
        <h2 className="text-[15px] font-bold tracking-[-0.01em]">Look up an old account</h2>
        <span className="ml-auto">
          <OldSiteStatus />
        </span>
      </div>
      <form onSubmit={submit} className="flex flex-wrap gap-2 px-4 py-3 sm:px-5">
        <label className="relative min-w-0 flex-1 basis-[260px]">
          <Search
            size={15}
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone"
          />
          <input
            className="input min-h-10 py-2 pl-9 text-[14px]"
            placeholder="Old email or old user id"
            aria-label="Old email or old user id"
            value={text}
            onChange={(e) => setText(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <button type="submit" className="btn-coin h-10 px-4 py-0 text-[14px]" disabled={!valid}>
          Look up
        </button>
      </form>
      {q && (
        <div className="border-t border-hairline">
          <Result result={result} />
        </div>
      )}
    </section>
  );
}

/**
 * On a claim: the old site's live record, loaded when staff press the button, or at once when
 * there is something to decide (`decide`: the buttons, shown under the evidence).
 */
export function OldAccountLoader({
  legacyUserId,
  autoLoad = false,
  decide,
}: {
  legacyUserId: string;
  autoLoad?: boolean;
  decide?: ReactNode;
}) {
  const [q, setQ] = useState<Query | null>(autoLoad ? { legacyUserId } : null);
  const result = useOldAccount(q);
  if (!q)
    return (
      <div className="border-t border-hairline px-4 py-3 sm:px-5">
        <button
          type="button"
          className="btn-ghost h-10 gap-2 px-4 py-0 text-[14px]"
          onClick={() => setQ({ legacyUserId })}
        >
          <CloudDownload size={15} aria-hidden="true" />
          Load the live record from the old site
        </button>
      </div>
    );
  return (
    <div className="border-t border-hairline">
      <Result result={result} decide={decide} />
    </div>
  );
}

function Result({
  result,
  decide,
}: {
  result: ReturnType<typeof useOldAccount>;
  decide?: ReactNode;
}) {
  const { data, error, isLoading, refetch } = result;
  if (isLoading) return <SkeletonRows rows={3} />;
  if (error)
    return (
      <div className="px-4 py-3 sm:px-5">
        <ErrorNote error={error} onRetry={() => void refetch()} />
      </div>
    );
  if (!data) return null;
  if (!data.found)
    return (
      <p className="px-4 py-4 text-[14px] text-stone sm:px-5">
        {data.ambiguous
          ? 'More than one old account has this email in different letter case. Look them up by old user id.'
          : 'No old account matches.'}
      </p>
    );
  return <OldAccountView data={data} decide={decide} />;
}

const OLD_STATE: Record<string, { label: string; tone: 'mint' | 'berry' | 'neutral' }> = {
  active: { label: 'Active there', tone: 'mint' },
  deleted: { label: 'Deleted there', tone: 'neutral' },
  banned: { label: 'Banned there', tone: 'berry' },
  blocked: { label: 'Blocked there', tone: 'berry' },
};

const PROFILE: [string, string][] = [
  ['role', 'Role'],
  ['visibility', 'Profile'],
  ['countryCode', 'Country'],
  ['isVerified', 'Verified'],
  ['idVerification', 'ID check'],
  ['faceVerification', 'Face check'],
  ['locationVerification', 'Location check'],
  ['blockReason', 'Block reason'],
  ['referBy', 'Referred by'],
  ['cachedTotalEarning', 'Cached earnings'],
  ['cachedTotalWithdrawal', 'Cached withdrawals'],
];

const show = (v: Scalar): string =>
  v === null || v === undefined || v === ''
    ? '—'
    : typeof v === 'boolean'
      ? v
        ? 'Yes'
        : 'No'
      : String(v);

const num = (v: Scalar) => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? fmtNum(Math.round(n * 1000) / 1000) : show(v);
};

const at = (v: Scalar) => (typeof v === 'number' ? v : null);

function IfLinked({ data }: { data: Extract<Lookup, { found: true }> }) {
  const r = data.ifLinkedNow;
  let tone = 'border-mint/40 bg-mint/10';
  let text: ReactNode;
  if ('refused' in r) {
    tone = 'border-berry/30 bg-berry/5';
    text = (
      <>
        <b>Linking now would be refused:</b> {r.refused}
      </>
    );
  } else if (r.status === 'pending_review')
    text = (
      <>
        <b>Linking now would wait for approval:</b> {fmtNum(r.coins)} coins (
        {fmtUsd(r.coins / 1000)}).
      </>
    );
  else if (r.status === 'credited')
    text = (
      <>
        <b>Linking now would credit {fmtNum(r.coins)} coins at once</b> ({fmtUsd(r.coins / 1000)}).
      </>
    );
  else text = <b>Linking now would link with no coins.</b>;
  return (
    <div role="note" className={`border-y px-4 py-2.5 text-[14px] leading-snug sm:px-5 ${tone}`}>
      {text}
    </div>
  );
}

export function OldAccountView({
  data,
  decide,
}: {
  data: Extract<Lookup, { found: true }>;
  decide?: ReactNode;
}) {
  const { old, numbers: n, here } = data;
  const state = OLD_STATE[old.state] ?? OLD_STATE.active!;
  const anomalies =
    n.anomalies.unreadableAmounts +
    n.anomalies.negativeWithdrawals +
    n.anomalies.unmatchedReversals;
  const profile = PROFILE.filter(([k]) => show(old.profile[k]) !== '—');
  return (
    <div>
      {data.pendingReview && <PendingReviewList r={data.pendingReview} decide={decide} />}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3 sm:px-5">
        <div className="min-w-0">
          <div className="truncate text-[15px] font-bold">{old.username ?? 'No username'}</div>
          <div className="truncate text-[13px] text-stone">{old.email}</div>
        </div>
        <Pill tone={state.tone}>{state.label}</Pill>
        <span className="ml-auto text-[13px] text-stone">
          <Mono>{old.legacyUserId}</Mono>
        </span>
      </div>
      {here ? (
        <div className="flex flex-wrap items-center gap-3 border-y border-hairline bg-[#fbf8f3] px-4 py-2.5 text-[14px] sm:px-5">
          <span className="text-stone">Linked here to</span>
          <UserChip id={here.userId} username={here.username} />
          {here.claimStatus && <StatusPill status={here.claimStatus} />}
          {here.claimCoins !== null && (
            <span className="tabular-nums">{fmtNum(here.claimCoins)} coins</span>
          )}
          <TimeAgo t={here.claimedAt} />
        </div>
      ) : (
        <IfLinked data={data} />
      )}
      <div className="grid gap-x-8 gap-y-5 px-4 py-4 sm:px-5 md:grid-cols-2">
        <section aria-label="Coins on the old site">
          <h3 className="mb-2.5 text-[13px] font-semibold text-stone">Coins, recomputed now</h3>
          <KV
            items={[
              [
                'Balance',
                <b key="b" className="tabular-nums">
                  {fmtNum(n.balanceCoins)} coins
                </b>,
              ],
              ['Unconfirmed (not moved)', `${fmtNum(n.pendingCoins)} coins`],
              ['Earned in total', `${fmtNum(n.lifetimeEarnedCoins)} coins`],
              ['Withdrawn in total', `${fmtNum(n.lifetimeWithdrawnCoins)} coins`],
              [
                'Activity',
                `${fmtNum(n.earningsCount)} credits · ${fmtNum(n.withdrawalsCount)} cash-outs`,
              ],
              [
                'Reversals',
                n.reversalsCount > 0 ? (
                  <b key="r" className="text-berry-deep">
                    {fmtNum(n.reversalsCount)}
                  </b>
                ) : (
                  '0'
                ),
              ],
              ['Last earning', <TimeAgo key="e" t={n.lastEarningAt} />],
              ['Last withdrawal', <TimeAgo key="w" t={n.lastWithdrawalAt} />],
              ...((n.ruleLastEarningAt ?? 0) !== (n.lastEarningAt ?? 0) ||
              (n.ruleLastWithdrawalAt ?? 0) !== (n.lastWithdrawalAt ?? 0)
                ? ([
                    [
                      'Activity before the move',
                      <TimeAgo
                        key="m"
                        t={Math.max(n.ruleLastEarningAt ?? 0, n.ruleLastWithdrawalAt ?? 0) || null}
                      />,
                    ],
                  ] as [string, ReactNode][])
                : []),
              ...(anomalies > 0
                ? ([
                    [
                      'Odd rows',
                      <span key="a" className="text-berry-deep">
                        {[
                          n.anomalies.unreadableAmounts &&
                            `${n.anomalies.unreadableAmounts} unreadable`,
                          n.anomalies.unmatchedReversals &&
                            `${n.anomalies.unmatchedReversals} reversals without their credit`,
                          n.anomalies.negativeWithdrawals &&
                            `${n.anomalies.negativeWithdrawals} negative withdrawals`,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>,
                    ],
                  ] as [string, ReactNode][])
                : []),
              ...(old.truncated
                ? ([
                    [
                      'Rows read',
                      <b key="t" className="text-berry-deep">
                        Stopped at {fmtNum(old.rowsRead.earnings)} earnings: check by hand
                      </b>,
                    ],
                  ] as [string, ReactNode][])
                : []),
            ]}
          />
        </section>
        <section aria-label="Old profile">
          <h3 className="mb-2.5 text-[13px] font-semibold text-stone">Old profile</h3>
          <KV
            items={[
              ['Created', <TimeAgo key="c" t={old.createdAt} />],
              ...profile.map(([k, label]) => [label, show(old.profile[k])] as [string, ReactNode]),
              [
                'Old site record',
                old.linkedTo
                  ? `Moved to ${old.linkedTo.newUserId}${old.linkedTo.state ? ` (${old.linkedTo.state.replace(/_/g, ' ')})` : ''}`
                  : 'Not moved',
              ],
            ]}
          />
        </section>
      </div>
      <RowsTable
        title={`Latest earnings (${fmtNum(old.recentEarnings.length)} of ${fmtNum(old.rowsRead.earnings)})`}
        head={['Offer', 'Network', 'Points', 'Status', 'When']}
        rows={old.recentEarnings.map((r) => [
          show(r.offerName ?? r.type),
          show(r.type),
          num(r.points),
          show(r.status),
          <TimeAgo key="t" t={at(r.createdAt)} />,
        ])}
      />
      <RowsTable
        title={`Latest withdrawals (${fmtNum(old.recentWithdrawals.length)} of ${fmtNum(old.rowsRead.withdrawals)})`}
        head={['Method', 'Amount', 'Points', 'Status', 'When']}
        rows={old.recentWithdrawals.map((r) => [
          show(r.method ?? r.type),
          r.amount === undefined || r.amount === null
            ? '—'
            : `${show(r.amount)} ${show(r.currency)}`,
          num(r.value),
          <span key="s" title={r.reason ? show(r.reason) : undefined}>
            {show(r.status)}
          </span>,
          <TimeAgo key="t" t={at(r.createdAt)} />,
        ])}
      />
    </div>
  );
}

function RowsTable({ title, head, rows }: { title: string; head: string[]; rows: ReactNode[][] }) {
  return (
    <section aria-label={title} className="border-t border-hairline">
      <h3 className="px-4 pb-1 pt-3 text-[13px] font-semibold text-stone sm:px-5">{title}</h3>
      {rows.length === 0 ? (
        <p className="px-4 pb-3 text-[14px] text-stone sm:px-5">None.</p>
      ) : (
        <ScrollX>
          <table className="data-table table-compact min-w-[560px]">
            <thead>
              <tr>
                {head.map((h, i) => (
                  <th
                    key={h}
                    className={i === 2 ? 'text-right' : i === head.length - 1 ? 'text-right' : ''}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((cells, i) => (
                <tr key={i}>
                  {cells.map((c, j) => (
                    <td
                      key={j}
                      className={
                        j === 0
                          ? 'max-w-[260px] truncate font-semibold'
                          : j === 2 || j === cells.length - 1
                            ? 'text-right tabular-nums'
                            : 'text-[13px]'
                      }
                    >
                      {c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollX>
      )}
    </section>
  );
}

/** Coins that were still pending on the old site, per claim (API: legacyPendingSummary). */
export interface OldPending {
  waiting: number;
  held: number;
  released: number;
  reversed: number;
  review: number;
  rejected: number;
  nextReleaseAt: number | null;
  notes: string[];
}

/** One line on a claim: where its old pending coins stand. */
export function OldPendingLine({ p, note }: { p: OldPending | null; note?: string | null }) {
  if (!p && !note) return null;
  const parts = p
    ? [
        p.waiting && `${fmtNum(p.waiting)} wait for this approval`,
        p.held && `${fmtNum(p.held)} held here`,
        p.released && `${fmtNum(p.released)} released`,
        p.reversed && `${fmtNum(p.reversed)} reversed on the old site`,
        p.review && `${fmtNum(p.review)} need a person`,
        p.rejected && `${fmtNum(p.rejected)} dropped with the rejection`,
      ].filter(Boolean)
    : [];
  return (
    <div className="text-[13px] leading-snug text-graphite">
      {parts.length > 0 && (
        <>
          <b className="font-semibold">Pending on the old site:</b> {parts.join(' · ')}
          {p?.nextReleaseAt ? (
            <>
              {' '}
              · next release <TimeAgo t={p.nextReleaseAt} />
            </>
          ) : null}
          .
        </>
      )}
      {p?.notes.length ? (
        <span className="text-stone"> {[...new Set(p.notes)].join(' ')}</span>
      ) : null}
      {note && <span className="block text-berry-deep">{note}</span>}
    </div>
  );
}

const ITEM_STATUS: Record<string, { label: string; tone: 'sun' | 'mint' | 'berry' | 'neutral' }> = {
  review: { label: 'Waits for you', tone: 'sun' },
  waiting: { label: 'Waits for claim approval', tone: 'neutral' },
  held: { label: 'Held until its date', tone: 'neutral' },
  released: { label: 'Released', tone: 'mint' },
  reversed: { label: 'Taken back', tone: 'berry' },
  rejected: { label: 'Dropped', tone: 'neutral' },
};

const day = (t: number) =>
  new Date(t).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }) + ' UTC';

/**
 * The old pending earnings this account brought over: what each was on the old site, where it
 * stands, and the warning signs found automatically. When some wait for a person, it is the
 * decision itself: advice, evidence, then the buttons.
 */
function PendingReviewList({ r, decide }: { r: PendingReview; decide?: ReactNode }) {
  const waiting = r.items.filter((i) => i.status === 'review');
  const coins = waiting.reduce((n, i) => n + i.coins, 0);
  const open = waiting.length > 0;
  // Waiting items first, then the rest in the order they came over.
  const items = [...waiting, ...r.items.filter((i) => i.status !== 'review')];
  return (
    <section
      aria-label="Pending coins brought over"
      className={`border-b border-hairline px-4 py-4 sm:px-5 ${open ? 'bg-white' : ''}`}
    >
      <div className="max-w-3xl">
        <h3 className={open ? 'text-[16px] font-bold' : 'text-[13px] font-semibold text-stone'}>
          {open
            ? `${fmtNum(coins)} coins wait for your decision`
            : 'Pending coins brought over from the old site'}
        </h3>
        <p className="mt-1 text-[13px] leading-snug text-stone">
          Earnings the old site still held when this account was linked.{' '}
          {open
            ? 'Release adds them to their balance; take back removes them for good.'
            : 'Each releases on its date once the old site confirms it.'}
        </p>

        {open && r.advice && (
          <div
            className={`mt-3 flex gap-2.5 rounded-lg border px-3.5 py-2.5 text-[14px] leading-snug ${
              r.advice.clear
                ? 'border-mint/40 bg-mint/10 text-mint-deep'
                : 'border-sun bg-sun/15 text-ink'
            }`}
          >
            <span
              aria-hidden="true"
              className={`mt-[5px] h-2 w-2 shrink-0 ${r.advice.clear ? 'bg-mint' : 'bg-sun'}`}
            />
            <span className="font-semibold">{r.advice.text}</span>
          </div>
        )}

        <ul className="mt-3 divide-y divide-hairline rounded-lg border border-hairline bg-white">
          {items.map((i) => {
            const st = ITEM_STATUS[i.status] ?? { label: i.status, tone: 'neutral' as const };
            return (
              <li
                key={i.id}
                className="grid grid-cols-[64px_minmax(0,1fr)] gap-x-3 sm:gap-x-4 px-3.5 py-3 sm:grid-cols-[104px_minmax(0,1fr)]"
              >
                <div className="text-right text-[15px] font-extrabold tabular-nums text-coin-ink">
                  {fmtNum(i.coins)}
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="min-w-0 break-words text-[14px] font-semibold">
                      {i.what ?? 'Unknown earning'}
                    </span>
                    <Pill tone={st.tone}>{st.label}</Pill>
                  </div>
                  <div className="mt-0.5 text-[13px] text-stone">
                    {[
                      i.network && i.network !== i.what ? i.network : null,
                      i.earnedAt && `Earned ${day(i.earnedAt)}`,
                    ]
                      .filter(Boolean)
                      .join(' · ') || 'No date on the old site'}
                  </div>
                  {i.status === 'review' && i.note && (
                    <div className="mt-0.5 text-[13px] text-stone">Waits because: {i.note}</div>
                  )}
                  <Signals list={i.signals} />
                </div>
              </li>
            );
          })}
        </ul>

        {r.account.length > 0 && (
          <div className="mt-3">
            <div className="text-[13px] font-semibold text-stone">About this account</div>
            <Signals list={r.account} />
          </div>
        )}

        {open && decide && <div className="mt-4 flex flex-wrap gap-2">{decide}</div>}
      </div>
    </section>
  );
}

/** Warning signs (berry marker) and plain facts (grey marker), one per line. */
function Signals({ list }: { list: PendingSignal[] }) {
  if (!list.length) return null;
  return (
    <ul className="mt-1.5 space-y-1 text-[13px] leading-snug">
      {list.map((s, n) => (
        <li
          key={n}
          className={`flex gap-2 ${s.tone === 'warn' ? 'font-medium text-berry-deep' : 'text-graphite'}`}
        >
          <span
            aria-hidden="true"
            className={`mt-[5px] h-2 w-2 shrink-0 ${s.tone === 'warn' ? 'bg-berry' : 'bg-stone/40'}`}
          />
          <span>
            {s.tone === 'warn' && <span className="sr-only">Warning: </span>}
            {s.text}
          </span>
        </li>
      ))}
    </ul>
  );
}
